import { REDDIT_SCRIPT_URL } from '../constants';
import { loadScript } from '../loadScript';
import type { TrackingEvent, TrackingEventName, TrackingProvider } from '../types';

type RdtCall = unknown[];

interface Rdt {
  (...args: RdtCall): void;
  sendEvent?: (...args: RdtCall) => void;
  callQueue: RdtCall[];
}

declare global {
  interface Window {
    rdt?: Rdt;
  }
}

const REDDIT_EVENTS: Record<TrackingEventName, string> = {
  view_item: 'ViewContent',
  begin_checkout: 'AddToCart',
  purchase: 'Purchase',
};

// Reddit's base-code stub: calls queue until pixel.js arrives and replays them.
function installStub(): Rdt {
  if (window.rdt) return window.rdt;
  const rdt: Rdt = Object.assign(
    (...args: RdtCall) => {
      if (rdt.sendEvent) rdt.sendEvent(...args);
      else rdt.callQueue.push(args);
    },
    { callQueue: [] },
  );
  window.rdt = rdt;
  return rdt;
}

function eventParams(event: TrackingEvent): Record<string, unknown> {
  return {
    itemCount: event.quantity,
    ...(event.value !== null ? { value: event.value, currency: event.currency } : {}),
    ...(event.orderId !== null ? { conversionId: event.orderId } : {}),
  };
}

export function createRedditProvider(pixelId: string): TrackingProvider {
  return {
    install() {
      const rdt = installStub();
      loadScript(REDDIT_SCRIPT_URL);
      // Decimal amounts: a 12.50 purchase goes in as 12.5, not as cents.
      rdt('init', pixelId, { optOut: false, useDecimalCurrencyValues: true });
      rdt('track', 'PageVisit');
    },
    track(event) {
      window.rdt?.('track', REDDIT_EVENTS[event.name], eventParams(event));
    },
  };
}
